import { useState, useRef, useEffect } from "react";
import { useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface EntityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function EntityDrawer({ isOpen, onClose, children }: EntityDrawerProps) {
  return (
    <div
      className={cn(
        "fixed inset-y-0 right-0 z-40 w-[580px] bg-background border-l shadow-2xl transition-transform duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}
    >
      <div className="h-full overflow-y-auto">
        <div className="sticky top-0 z-10 flex justify-end p-3 bg-background/80 backdrop-blur-sm">
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-6 pb-6">
          {children}
        </div>
      </div>
    </div>
  );
}

export function useDrawerEntity<T>() {
  const [drawerEntity, setDrawerEntity] = useState<T | null>(null);
  const lastDrawerEntity = useRef<T | null>(null);
  if (drawerEntity) lastDrawerEntity.current = drawerEntity;
  const displayEntity = drawerEntity || lastDrawerEntity.current;
  return { drawerEntity, setDrawerEntity, displayEntity };
}

export function useDrawerSelect(setDrawerEntity: (id: string) => void) {
  const searchString = useSearch();
  const selectId = new URLSearchParams(searchString).get("select");

  useEffect(() => {
    if (selectId) {
      setDrawerEntity(selectId);
    }
  }, [selectId, setDrawerEntity]);
}
